import { useEffect, useRef, useState } from "react";
import logoAgro from "../../public/assets/icons/logoAgro.png";
import {
  FarmerFieldsStates,
  Service,
  WeatherStationStates,
} from "@helpers/ui_helper";
import { Button, Form, Input, Modal, Select, Tour } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getFarmersFields,
  getStaticCities,
  getStaticProvinces,
  getStaticsProducts,
  growingSeasonField,
  registerField,
  updateField,
} from "../../services/api";
import crossWhite from "../../public/assets/icons/cross-white.svg";
import tikwhite from "../../public/assets/icons/Tik-white.svg";
import { fieldViewSteps } from "../../tour_steps/fields";
import {
  EditOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import moment from "jalali-moment";
import GrowingSeason from "../../features/growing-season/presentation/pages/GrowingSeason";
import AgroMapFarm from "../../features/map/presentation/components/AgroMapFarm";
import { toolbarBaseControls } from "../../features/map/utils/configs";
import { FieldMarkerIcon } from "../../components/map/utils/customs";

const { TextArea } = Input;

function CreateOrEditField({
  setFarmerFieldsState,
  seasonObject,
  defaultData,
  data,
}) {
  const [open, setOpen] = useState(false);
  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const [form] = Form.useForm();

  let initialValue = {
    farmId: defaultData?.id ?? null,
    name: defaultData?.name ?? "",
    ownershipTypeId: defaultData?.ownershipTypeId ?? null,
    productId: defaultData?.productId
      ? defaultData?.productId
      : defaultData?.isBare
      ? -1
      : null,
    cultivationTypeId: defaultData?.cultivationTypeId ?? null,
    irrigationSourceId: defaultData?.irrigationSourceId ?? null,
    provinceId: defaultData?.provinceId ?? null,
    cityId: defaultData?.cityId ?? null,
    zone: defaultData?.zone ?? "",
    description: defaultData?.description ?? "",
    polygon: data?.polygon ?? [],
    centerCoordinates: data?.centerCoordinates ?? [],
    area: data?.area ?? 0,
    isBare: defaultData?.isBare ?? false,
  };

  const [ownershipTypes, setOwnershipTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [cultivationTypes, setCultivationTypes] = useState([]);
  const [irrigationSources, setIrrigationSources] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [provinceId, setProvinceId] = useState(defaultData?.provinceId);
  const [saveField, setSaveField] = useState(false);
  const [infoField, setInfoField] = useState();
  const [openModal, setOpenModal] = useState(false);

  const queryClient = useQueryClient();

  const staticProductsQuery = useQuery({
    queryKey: ["staticProducts"],
    queryFn: getStaticsProducts,
  });

  const staticProvincesQuery = useQuery(
    ["staticsProvinces"],
    getStaticProvinces
  );

  const staticCitiesQuery = useQuery(["staticCities"], getStaticCities);
  const farmersFieldsQuery = useQuery(["farmersFields"], getFarmersFields);

  const registerFieldMutation = useMutation(registerField, {
    onSuccess: (data) => {
      setSaveField(true);
      setInfoField({
        farmId: data?.farm?.id,
        polygon: data?.farm?.polygon,
        centerCoordinates: data?.farm?.centerCoordinates,
        area: data?.farm?.area,
      });
      seasonObject
        ? submitGrowingSeason(
            data?.farm?.id,
            data?.farm?.polygon,
            data?.farm?.centerCoordinates,
            data?.farm?.area
          )
        : setFarmerFieldsState({
            service: Service.FieldView,
            state: FarmerFieldsStates.AddFieldIntro,
            fieldObject: null,
          });
      queryClient.invalidateQueries(["farmersFields", "fields"]);
    },
  });

  const updateFieldMutation = useMutation(updateField, {
    onSuccess: (data) => {
      setSaveField(true);
      setInfoField({
        farmId: data?.farm?.id,
        polygon: data?.farm?.polygon,
        centerCoordinates: data?.farm?.centerCoordinates,
        area: data?.farm?.area,
      });
      seasonObject
        ? submitGrowingSeason(
            data?.farm?.id,
            data?.farm?.polygon,
            data?.farm?.centerCoordinates,
            data?.farm?.area
          )
        : setFarmerFieldsState({
            service: Service.FieldView,
            state: FarmerFieldsStates.AddFieldIntro,
            fieldObject: null,
          });
      queryClient.invalidateQueries(["farmersFields", "fields"]);
    },
  });
  const addGrowingSeasonMutation = useMutation(
    ["growingSeason"],
    growingSeasonField,
    {
      onSuccess: () => {
        setFarmerFieldsState({
          service: Service.FieldView,
          state: FarmerFieldsStates.AddFieldIntro,
          fieldObject: null,
        });
        queryClient.invalidateQueries([
          "getDataAllCertificate",
          infoField?.farmId,
        ]);
      },
    }
  );

  const submitFarm = () => {
    if (!saveField) {
      // for updating farm
      if (defaultData && (defaultData?.id || defaultData?.farmId)) {
        // if defaultData has id, it comes from server
        let filedToRegister = form.getFieldsValue(true);

        // not latLngTypePolygonCoordinates!
        let simpleTypePolygonCoordinates = [];

        if (Array.isArray(form.getFieldValue("polygon")[0])) {
          simpleTypePolygonCoordinates = form.getFieldValue("polygon");
        } else {
          simpleTypePolygonCoordinates = form.getFieldValue("polygon");
        }
        filedToRegister.polygon = simpleTypePolygonCoordinates;
        // filedToRegister.centerCoordinates = filedToRegister.centerCoordinates;

        updateFieldMutation.mutate({
          data: form.getFieldsValue(true),
          farmId: defaultData?.id,
        });
      }
      // for adding farm
      else {
        let filedToRegister = form.getFieldsValue(true);
        filedToRegister.polygon = form.getFieldValue("polygon");

        registerFieldMutation.mutate(filedToRegister);
      }
    } else {
      submitGrowingSeason(
        infoField?.farmId,
        infoField?.polygon,
        infoField?.centerCoordinates,
        infoField?.area
      );
    }
  };
  const submitGrowingSeason = (farmId, polygon, centerCoordinates, area) => {
    addGrowingSeasonMutation.mutate({
      growingSeasonData: {
        ...seasonObject,
        polygon,
        centerCoordinates,
        area,
      },
      farmId,
    });
  };
  const onSubmitForm = (season) => {
    setFarmerFieldsState((prev) => ({
      ...prev,
      seasonObject: season,
    }));
    handleCloseModal();
  };
  const handleShowModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  //mock data
  useEffect(() => {
    if (farmersFieldsQuery?.data) {
      setOwnershipTypes(
        farmersFieldsQuery?.data?.farmersFields["OWNERSHIP_TYPE"].map((ot) => ({
          label: ot.name,
          value: ot.id,
        }))
      );

      setCultivationTypes(
        farmersFieldsQuery?.data?.farmersFields["CULTIVATION_TYPE"].map(
          (ct) => ({ label: ct.name, value: ct.id })
        )
      );

      setIrrigationSources(
        farmersFieldsQuery?.data?.farmersFields["IRRIGATION_SOURCES"].map(
          (it) => ({ label: it.name, value: it.id })
        )
      );
    }
  }, [farmersFieldsQuery?.data]);

  useEffect(() => {
    if (staticProductsQuery?.data) {
      let products = staticProductsQuery?.data?.map((item) => {
        return { label: item.persianName, value: item.id };
      });
      products.unshift({ label: "زمین آیش است", value: -1 });
      setProducts(products);
    }
  }, [staticProductsQuery?.data]);

  useEffect(() => {
    if (staticProvincesQuery?.data) {
      let provs = staticProvincesQuery?.data?.map((province) => {
        return { label: province.name, value: province.id };
      });
      setProvinces(provs);
    }
  }, [staticProvincesQuery?.data]);

  useEffect(() => {
    if (provinceId) {
      let cities = staticCitiesQuery?.data
        ?.filter((city) => city.provinceId === provinceId)
        .map((c) => {
          return { label: c.name, value: c.id };
        });
      setCities(cities);
    }
  }, [provinceId, staticProvincesQuery?.data, staticCitiesQuery?.data]);

  const fieldvalue = form?.getFieldValue("polygon")?.length;

  return (
    <div className="flex max-w-[50rem] mx-auto flex-col gap-4 items-center justify-center pt-4">
      <p className="text-agrotext font-medium text-right text-sm">
        <span className="text-[#ff4d4f]">* </span>
        لطفا اطلاعات هر قطعه زمین خود را به صورت جداگانه ثبت نمائید
      </p>

      <div className="box-shadow p-[6px] !rounded-[16px] w-fit h-fit">
        <div
          ref={ref1}
          className="inner-circle-neo relative p-1 flex flex-col-reverse max-lg:flex-col-reverse !rounded-[16px] justify-center items-center gap-2"
        >
          <Form
            autoComplete="nope"
            className="flex flex-wrap relative p-4 border-2 max-lg:p-2 mt-10 !h-full gap-y-1 max-lg:gap-x-4 justify-between items-start max-lg:justify-center"
            form={form}
            onFinish={submitFarm}
            initialValues={initialValue}
          >
            <div className="absolute pl-24 font-bold bg-[#B3DACC] border-2 border-[#DDE6EF] rounded-t-lg border-b-0 right-[-3px] top-[-34px] px-3 py-1">
              اطلاعات قطعه
            </div>

            <div className="flex w-[220px] max-lg:w-[46%] h-[100px] max-lg:h-fit flex-col justify-start items-start gap-2">
              <p className=" text-right text-zinc-700 text-base max-lg:text-xs font-medium">
                <span className="text-[#ff4d4f]">* </span>
                نام قطعه
              </p>

              <Form.Item
                name="name"
                rules={[
                  {
                    validator(rule, value) {
                      return new Promise((resolve, reject) => {
                        if (value?.length >= 2) {
                          resolve();
                        } else {
                          reject("نام قطعه باید بیشتر از دو حرف باشد");
                        }
                      });
                    },
                  },
                ]}
              >
                <Input
                  autoComplete="nope"
                  placeholder="نام قطعه را وارد کنید"
                  className="self-stretch pl-24 pr-4 py-2 h-8 rounded-3xl max-lg:p-1 max-lg:text-[12px] input-neo outline-none justify-end items-center gap-2.5 flex"
                  onChange={(e) => {
                    setFarmerFieldsState((prev) => ({
                      ...prev,
                      defaultData: { ...defaultData, name: e.target.value },
                    }));
                    form.setFieldValue("name", e?.target?.value);
                  }}
                />
              </Form.Item>
            </div>

            {/* ownershipType */}
            <div className="flex w-[220px] max-lg:w-[46%] h-[100px] max-lg:h-fit flex-col justify-start items-start gap-2">
              <p className=" text-right text-zinc-700 text-base max-lg:text-xs font-medium">
                نوع مالکیت
              </p>
              <Form.Item name="ownershipTypeId" className=" w-full">
                <Select
                  autoComplete="nope"
                  showSearch
                  optionFilterProp="label"
                  className="button-neo !cursor-pointer self-stretch px-4 py-1 rounded-3xl max-lg:p-1 max-lg:text-[12px] justify-between items-center flex text-xs"
                  options={ownershipTypes}
                  onChange={(value) => {
                    setFarmerFieldsState((prev) => ({
                      ...prev,
                      defaultData: { ...defaultData, ownershipTypeId: value },
                    }));

                    form.setFieldValue("ownershipTypeId", value);
                  }}
                  placeholder="انتخاب کنید"
                />
              </Form.Item>
            </div>
            {/* irrigationSourceType */}
            <div className="flex w-[220px] max-lg:w-[46%] h-[80px] max-lg:h-fit flex-col justify-start items-start gap-2">
              <div className=" text-right text-zinc-700 text-base max-lg:text-xs font-medium">
                <span className="text-[#ff4d4f]">* </span>
                منبع آبی
              </div>

              <Form.Item
                rules={[
                  { required: true, message: "نوع منبع آبی را انتخاب کنید" },
                ]}
                name="irrigationSourceId"
                className=" w-full"
              >
                <Select
                  autoComplete="nope"
                  showSearch
                  optionFilterProp="label"
                  className="button-neo !cursor-pointer self-stretch px-4 py-1 rounded-3xl max-lg:p-1 max-lg:text-[12px] justify-between items-center flex text-xs"
                  options={irrigationSources}
                  onChange={(value) => {
                    form.setFieldValue("irrigationSourceId", value);
                    setFarmerFieldsState((prev) => ({
                      ...prev,
                      defaultData: {
                        ...defaultData,
                        irrigationSourceId: value,
                      },
                    }));
                  }}
                  placeholder="انتخاب کنید"
                />
              </Form.Item>
            </div>

            {/* province */}
            <div className="flex w-[220px] max-lg:w-[46%] h-[80px] max-lg:h-fit flex-col justify-start items-start gap-2">
              <p className=" text-right text-zinc-700 text-base max-lg:text-xs font-medium">
                <span className="text-[#ff4d4f]">* </span>
                استان (محل مزرعه)
              </p>

              <Form.Item
                name="provinceId"
                rules={[
                  { required: true, message: "نام استان را انتخاب کنید" },
                ]}
                className=" w-full"
              >
                <Select
                  autoComplete="nope"
                  showSearch
                  optionFilterProp="label"
                  className="provinces button-neo self-stretch px-4 py-1 rounded-3xl max-lg:p-1 max-lg:text-[12px] justify-between items-center flex"
                  options={provinces}
                  onChange={(value) => {
                    setFarmerFieldsState((prev) => ({
                      ...prev,
                      defaultData: {
                        ...defaultData,
                        provinceId: value,
                        cityId: null,
                      },
                    }));

                    form.setFieldValue("provinceId", value);
                    form.setFieldValue("cityId", null);
                    setProvinceId(value);
                  }}
                  placeholder="انتخاب استان"
                />
              </Form.Item>
            </div>

            {/* city */}
            <div className="flex w-[220px] max-lg:w-[46%] h-[80px] max-lg:h-fit flex-col justify-start items-start gap-2">
              <p className=" text-right text-zinc-700 text-base max-lg:text-xs font-medium">
                <span className="text-[#ff4d4f]">* </span>
                شهر (محل مزرعه)
              </p>

              <Form.Item
                name="cityId"
                rules={[{ required: true, message: "نام شهر را انتخاب کنید" }]}
                className=" w-full"
              >
                <Select
                  autoComplete="nope"
                  showSearch
                  optionFilterProp="label"
                  className="button-neo !cursor-pointer self-stretch px-4 py-1 rounded-3xl max-lg:p-1 max-lg:text-[12px] justify-between items-center flex text-xs"
                  options={cities}
                  onChange={(value) => {
                    setFarmerFieldsState((prev) => ({
                      ...prev,
                      defaultData: { ...defaultData, cityId: value },
                    }));

                    form.setFieldValue("cityId", value);
                  }}
                  placeholder="انتخاب شهر"
                />
              </Form.Item>
            </div>

            {/* zone */}
            <div className="flex w-[220px] max-lg:w-[46%] h-[80px] max-lg:h-fit flex-col justify-start items-start gap-2">
              <div className="text-right text-zinc-700 text-base max-lg:text-xs font-medium">
                منطقه
              </div>
              <Form.Item name="zone" className=" w-full">
                <Input
                  autoComplete="nope"
                  placeholder="نام منطقه را وارد کنید"
                  className="self-stretch pl-24 pr-4 py-2 h-8 rounded-3xl max-lg:p-1 max-lg:text-[12px] input-neo outline-none justify-end items-center gap-2.5 flex"
                  onChange={(e) => {
                    setFarmerFieldsState((prev) => ({
                      ...prev,
                      defaultData: { ...defaultData, zone: e?.target?.value },
                    }));
                    form.setFieldValue("zone", e?.target?.value);
                  }}
                />
              </Form.Item>
            </div>

            {/* date
            <div className="flex w-[220px] max-lg:w-[46%] h-[80px] max-lg:h-fit flex-col justify-start items-start gap-2">
              <div className="text-right text-zinc-700 text-base max-lg:text-xs font-medium">
              <span className="text-[#ff4d4f]">* </span>

                تاریخ
              </div>
              <Form.Item name="changeAt"   rules={[
                  { required: true, message: "تاریخ ایجاد زمین  را انتخاب کنید" },
                ]} className=" w-full">
              <ConfigProvider locale={fa_IR} direction="rtl">
              <JalaliLocaleListener />
              <DatePickerJalali
              
                className="inner-box-hover px-4 py-1.5 w-[220px]   max-lg:w-[250px]"
                onChange={(e) => {
                  const date = moment(e?.$d).format("YYYY-MM-DD");
                  form.setFieldValue("changeAt",date)
                }}
                format="YYYY/MM/DD"
              />
            </ConfigProvider>
              </Form.Item>
            </div> */}
            {/* area */}
            {/* <div className="flex w-[220px] max-lg:w-[46%] h-[80px] max-lg:h-fit flex-col justify-start items-start gap-2">
              <p className=" h-full pt-1 text-right flex text-[#404848] font-semibold text-base max-lg:text-xs">
                مساحت:
              </p>
              <Form.Item
                name="area"
                className="  flex items-center justify-center  h-full"
              >
                {initialValue.area ? (
                  <Input
                    autoComplete="nope"
                    disabled
                    showSearch
                    optionFilterProp="label"
                    className="self-stretch pl-24 pr-4 py-2 h-8 rounded-3xl max-lg:p-1 max-lg:text-[12px] input-neo outline-none justify-end items-center gap-2.5 flex"
                    options={cultivationTypes}
                    onChange={(value) => {
                      form.setFieldValue("cultivationTypeId", value);
                    }}
                    placeholder="انتخاب کنید"
                  />
                ) : (
                  <p className="  h-full    px-1 rounded-3xl max-lg:p-1 max-lg:text-[12px] justify-center items-center flex">
                    محدوده زمین را تعیین کنید
                  </p>
                )}
              </Form.Item>
            </div> */}
            {/* more info */}
            <div className="flex w-full flex-col justify-start items-start gap-2">
              <p className=" text-right text-zinc-700 text-base max-lg:text-xs font-medium">
                توضیحات بیشتر
              </p>
              <Form.Item name="description" className="!w-full">
                <TextArea
                  autoComplete="nope"
                  placeholder="در صورت تمایل توضیحات خود را وارد کنید"
                  className="round !rounded-2xl max-lg:p-1 max-lg:text-[12px] input-neo outline-none"
                  autoSize={{
                    minRows: 1,
                    maxRows: 4,
                  }}
                  onChange={(e) => {
                    setFarmerFieldsState((prev) => ({
                      ...prev,
                      defaultData: {
                        ...defaultData,
                        description: e?.target?.value,
                      },
                    }));
                    form.setFieldValue("description", e?.target?.value);
                  }}
                />
              </Form.Item>
            </div>

            {/*  add Growing seasons   */}
            {seasonObject && !openModal ? (
              <div
                onClick={handleShowModal}
                className="w-full flex flex-row rounded-2xl my-4 justify-between cursor-pointer hover:brightness-105 duration-500  card-shadow "
              >
                <div className="flex justify-between w-full p-4">
                  <div className="flex flex-wrap items-center gap-1">
                    <img src={logoAgro} alt="" className="w-5 h-5" />
                    <span className="font-semibold text-[#404848]">
                      فصل رشد{" "}
                      {seasonObject?.currentGrowingSeason?.season === "بهاره"
                        ? +moment(
                            seasonObject?.currentGrowingSeason?.startMonth
                          )
                            .locale("fa")
                            .format("YYYY") + 1
                        : moment(seasonObject?.currentGrowingSeason?.startMonth)
                            .locale("fa")
                            .format("YYYY")}{" "}
                      {/* {season?.formType === "farm" && (
                        <span>({season?.productLable})</span>
                      )} */}
                    </span>
                  </div>
                  <span className="bg-[#B3DACC] w-32 font-semibold text-[#434343] py-[2px] rounded-xl">
                    {seasonObject?.product?.persianName}
                  </span>
                </div>
              </div>
            ) : (
              <div
                onClick={handleShowModal}
                className="flex flex-row items-center justify-center w-full cursor-pointer hover:brightness-105 gap-2 text-right text-agrotext text-[15px] max-lg:text-[13.5px] font-semibold"
              >
                افزودن فصل رشد جدید
                <div className="addbtn pt-1 flex items-center justify-center w-7 h-7 rounded-full card-shadow">
                  +
                </div>
              </div>
            )}

            {/* buttons */}
            <section className="flex flex-row w-full justify-between mb-[-2rem] gap-2">
              <Form.Item htmlFor="submit" className="!h-fit">
                <Button
                  ref={ref3}
                  htmlType="submit"
                  className="Submitpayment hover:brightness-110 !border-slate-200 px-8 h-[44px] max-lg:w-[150px] button-green justify-center items-center gap-1 flex"
                  loading={
                    registerFieldMutation?.isLoading ||
                    updateFieldMutation?.isLoading
                  }
                  disabled={initialValue.polygon.length === 0}
                >
                  <img
                    src={tikwhite}
                    alt="tik-icon"
                    className="w-[20px] h-[20px]"
                  />

                  <p className="text-center w-[120px] text-white text-base max-lg:text-xs font-bold font-body">
                    تایید و ثبت زمین
                  </p>
                </Button>
              </Form.Item>

              <Form.Item htmlFor="submit" className="!h-fit">
                <Button
                  loading={
                    registerFieldMutation?.isLoading ||
                    updateFieldMutation?.isLoading
                  }
                  className="Cancel hover:brightness-110 px-8 h-[44px] max-lg:w-[150px] button-red border !border-slate-200 justify-center items-center gap-1 flex"
                  onClick={() =>
                    setFarmerFieldsState({
                      service: Service.Weather,
                      state: WeatherStationStates.AddStationIntro,
                      fieldObject: null,
                      defaultData: null,
                    })
                  }
                >
                  <img
                    src={crossWhite}
                    alt="cancel-icon"
                    className="w-[20px] h-[20px]"
                  />

                  <p className=" text-center w-[120px] text-white text-base max-lg:text-xs font-bold">
                    بازگشت
                  </p>
                </Button>
              </Form.Item>
            </section>
          </Form>
          {/*Modal Add Growing Season */}
          <Modal
            open={openModal}
            // onCancel={handleCloseModal}
            closeIcon={false}
            footer={null}
            width={"800px"}
          >
            <GrowingSeason
              type="addModal"
              season={seasonObject}
              cancel={handleCloseModal}
              onSubmitForm={onSubmitForm}
            />
          </Modal>

          <div className="Earth p-2 agrobg rounded-2xl max-lg:w-full justify-center items-center gap-4 flex max-lg:flex-col">
            <div className="Borderin self-stretch agrobg rounded-lg inner-box-shadow border max-lg:flex-col justify-center items-center gap-4 flex overflow-hidden">
              <div
                ref={ref2}
                className="Map relative w-[750px] max-lg:w-full h-full rounded-tl-2xl rounded-tr-2xl items-center flex flex-col"
              >
                <div
                  className={`relative w-full h-[340px] max-lg:w-full max-lg:h-[20rem] max-lg:flex-col 
                    ${fieldvalue === 0 && "blur-[2px] opacity-50 "} 
                    pointer-events-none`}
                >
                  <AgroMapFarm
                    type="onlyShow"
                    mapId="452452245"
                    toolbarOptions={toolbarBaseControls}
                    markerIcon={FieldMarkerIcon}
                    currentFarmPolygon={data?.polygon}
                  />
                </div>
                {fieldvalue === 0 ? (
                  <div
                    className="absolute top-14 cursor-pointer flex flex-col gap-10 items-center justify-center"
                    onClick={() =>
                      setFarmerFieldsState((prev) => ({
                        ...prev,
                        service: Service.FieldRegistration,
                        state: FarmerFieldsStates.DrawPolygon,
                        fieldObject: form.getFieldsValue(true),
                        defaultData: defaultData,
                      }))
                    }
                  >
                    <div className="flex items-center justify-center text-white pt-2 text-[106px] font-light w-44 h-44 border-8 border-white rounded-xl">
                      <PlusOutlined />
                    </div>
                    <Button
                      loading={
                        registerFieldMutation?.isLoading ||
                        updateFieldMutation?.isLoading
                      }
                      className="button-green hover:brightness-110 !border-slate-200 py-1 px-10 !text-white cursor-pointer"
                      onClick={() =>
                        setFarmerFieldsState((prev) => ({
                          ...prev,
                          service: Service.FieldRegistration,
                          state: FarmerFieldsStates.DrawPolygon,
                          fieldObject: form.getFieldsValue(true),
                          defaultData: defaultData,
                        }))
                      }
                    >
                      <p className="text-center text-white font-bold font-body">
                        {fieldvalue === 0
                          ? "تعیین محدوده زمین"
                          : "ویرایش محدوده زمین"}
                      </p>
                    </Button>
                  </div>
                ) : (
                  <Button
                    loading={
                      registerFieldMutation?.isLoading ||
                      updateFieldMutation?.isLoading
                    }
                    className="absolute bottom-4 z-[999] button-green hover:brightness-110 !border-slate-200 py-1 px-10 text-center !text-white font-bold font-body cursor-pointer"
                    onClick={() =>
                      setFarmerFieldsState((prev) => ({
                        ...prev,
                        service: Service.FieldRegistration,
                        state: FarmerFieldsStates.DrawPolygon,
                        fieldObject: form.getFieldsValue(true),
                        defaultData: defaultData,
                      }))
                    }
                  >
                    <EditOutlined />
                    ویرایش محدوده زمین
                  </Button>
                )}

                <Button
                  loading={
                    registerFieldMutation?.isLoading ||
                    updateFieldMutation?.isLoading
                  }
                  hidden={open}
                  className="circle-neo flex items-center justify-center text-[#008a44] hover:!text-[#50aa44] font-bold absolute left-0 top-4 !rounded-l-none !rounded-r-xl z-[999]"
                  icon={<QuestionCircleOutlined className="text-base" />}
                  onClick={() => setOpen(true)}
                >
                  راهنما
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {open ? (
        <Tour
          type="primary"
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          steps={fieldViewSteps(ref1, ref2, ref3)}
        />
      ) : null}
    </div>
  );
}

export default CreateOrEditField;
